// ✅ src/pages/VehicleV2/JobCardListing.jsx  (UPDATED DESIGN TEMPLATE)
// Backend expected:
//   app.use("/api/jobCardListing", jobCardListing);
//
// APIs used:
//   GET  https://nissi-india.com/api/jobCardListing/init?loginUserId=..&branchId=..&branchType=..&selectedBranchId=..
//   GET  https://nissi-india.com/api/jobCardListing/list
//   POST https://nissi-india.com/api/jobCardListing/delete
//
// Design template aligned with ProductionBOMListing:
// - Search Criteria filter panel (blue-50 / gray-800/50)
// - Chevron show/hide filters
// - Striped table + hover rows
// - Edit / View icons + Select All last column
// - Red Remove button under table
// Functional rules kept:
// - no Add New button
// - View -> /vehicle/JobCardEntryDetail?vehicleId=<id>
// - Edit -> /vehicle/JobCardEntry?vehicleId=<id>

import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaTrash,
  FaEye,
  FaEdit,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/jobCardListing";

const toText = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v);
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(v ?? "").trim();

const JobCardListing = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid") || "8";
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  const branchId = useMemo(() => toInt(branchIdLS, 8), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim() || "HO",
    [branchTypeLS]
  );
  const userId = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);
  const loginUserId =
    (typeof window !== "undefined"
      ? sessionStorage.getItem("loginUserId")
      : "") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const [branches, setBranches] = useState([]);
  const [stores, setStores] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);

  const [filters, setFilters] = useState({
    selectedBranchId: "Select",
    storeId: "Select",
    vehicleTypeIndex: "Select",
    vehicleRegNo: "",
    vehicleName: "",
    vehicleCode: "",
  });

  const [search, setSearch] = useState("");
  const [jobCards, setJobCards] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const totalPages = useMemo(() => {
    const t = Math.ceil(total / limit);
    return t > 0 ? t : 1;
  }, [total, limit]);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const mapBranchOption = useCallback((b) => {
    return {
      value: toText(b?.value ?? b?.id ?? b?.branchId ?? ""),
      label: toText(b?.label ?? b?.name ?? b?.branchName ?? ""),
    };
  }, []);

  const mapStoreOption = useCallback((s) => {
    return {
      value: toText(s?.value ?? s?.id ?? s?.storeId ?? ""),
      label: toText(s?.label ?? s?.name ?? s?.storeName ?? ""),
    };
  }, []);

  const mapVehicleTypeOption = useCallback((v) => {
    return {
      value: toText(v?.value ?? v?.id ?? v?.vehicleTypeIndex ?? ""),
      label: toText(v?.label ?? v?.name ?? v?.vehicleTypeName ?? ""),
    };
  }, []);

  const mapRow = useCallback((r) => {
    return {
      id: toInt(r?.id ?? r?.jobCardId ?? r?.vehicleId ?? 0),
      vehicleMainType: toText(r?.vehicleMainType ?? r?.fld_vehiclemaintype),
      vehicleCode: toText(r?.vehicleCode ?? r?.fld_vehiclecode),
      vehicleName: toText(r?.vehicleName ?? r?.fld_vehiclename),
      vehiclePortNo: toText(
        r?.vehiclePortNo ?? r?.vehicleRegNo ?? r?.fld_vehicleportno
      ),
      jobCardNo: toText(r?.jobCardNo ?? r?.fld_jobcardno),
      status: toText(r?.status ?? r?.fld_status),
      location: toText(r?.location ?? r?.fld_location),
    };
  }, []);

  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          loginUserId: toInt(loginUserId, 0),
          branchId: toInt(branchId, 0),
          branchType: clean(branchType),
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Init failed");
      }

      const branchOptions = [
        { value: "Select", label: "Select" },
        ...(Array.isArray(data?.branches)
          ? data.branches.map(mapBranchOption)
          : []),
      ];

      const storeOptions = [
        { value: "Select", label: "Select" },
        ...(Array.isArray(data?.stores) ? data.stores.map(mapStoreOption) : []),
      ];

      const vehicleTypeOptions = [
        { value: "Select", label: "Select" },
        ...(Array.isArray(data?.vehicleTypes)
          ? data.vehicleTypes.map(mapVehicleTypeOption)
          : []),
      ];

      setBranches(branchOptions);
      setStores(storeOptions);
      setVehicleTypes(vehicleTypeOptions);

      if (clean(branchType).toUpperCase() !== "HO") {
        const myBranch = branchOptions.find(
          (b) => String(b.value) === String(branchId)
        );

        setFilters((prev) => ({
          ...prev,
          selectedBranchId: myBranch ? myBranch.value : String(branchId),
        }));
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Init failed"
      );
    } finally {
      setPageLoading(false);
    }
  }, [
    loginUserId,
    branchId,
    branchType,
    commonHeaders,
    mapBranchOption,
    mapStoreOption,
    mapVehicleTypeOption,
  ]);

  const loadStoresForBranch = useCallback(
    async (branchVal) => {
      try {
        const { data } = await axios.get(`${API_BASE}/init`, {
          params: {
            loginUserId: toInt(loginUserId, 0),
            branchId: toInt(branchId, 0),
            branchType: clean(branchType),
            selectedBranchId: branchVal !== "Select" ? branchVal : "",
          },
          headers: commonHeaders,
        });

        if (!data?.success) return;

        const storeOptions = [
          { value: "Select", label: "Select" },
          ...(Array.isArray(data?.stores)
            ? data.stores.map(mapStoreOption)
            : []),
        ];

        setStores(storeOptions);
      } catch (err) {
        console.error(err);
      }
    },
    [loginUserId, branchId, branchType, commonHeaders, mapStoreOption]
  );

  const loadList = useCallback(
    async (nextPage = page, nextLimit = limit, overrideFilters = null) => {
      try {
        setLoading(true);

        const f = overrideFilters || filters;

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            loginUserId: toInt(loginUserId, 0),
            branchId: toInt(branchId, 0),
            branchType: clean(branchType),
            selectedBranchId:
              f.selectedBranchId !== "Select" ? f.selectedBranchId : "",
            storeId: f.storeId !== "Select" ? f.storeId : "",
            vehicleTypeIndex:
              f.vehicleTypeIndex !== "Select" ? f.vehicleTypeIndex : "",
            vehicleRegNo: clean(f.vehicleRegNo),
            vehicleName: clean(f.vehicleName),
            vehicleCode: clean(f.vehicleCode),
            search: clean(search),
            page: toInt(nextPage, 1),
            limit: toInt(nextLimit, 10),
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load list");
        }

        const list = Array.isArray(data?.list) ? data.list.map(mapRow) : [];

        setJobCards(list);
        setTotal(toInt(data?.total, 0));
        setPage(toInt(data?.page, nextPage));
        setLimit(toInt(data?.limit, nextLimit));
        setSelectedIds([]);
        setSelectAll(false);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load list"
        );
        setJobCards([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [
      page,
      limit,
      filters,
      loginUserId,
      branchId,
      branchType,
      search,
      commonHeaders,
      mapRow,
    ]
  );

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    loadList(page, limit);
  }, [page, limit, loadList]);

  useEffect(() => {
    const idsOnPage = jobCards.map((x) => x.id).filter(Boolean);
    setSelectAll(
      idsOnPage.length > 0 && idsOnPage.every((id) => selectedIds.includes(id))
    );
  }, [selectedIds, jobCards]);

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }

    const ids = jobCards.map((x) => x.id).filter(Boolean);
    setSelectedIds(ids);
    setSelectAll(true);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSearchClick = () => {
    setPage(1);
    loadList(1, limit);
  };

  const handleViewAll = () => {
    const cleared = {
      selectedBranchId:
        clean(branchType).toUpperCase() !== "HO" ? String(branchId) : "Select",
      storeId: "Select",
      vehicleTypeIndex: "Select",
      vehicleRegNo: "",
      vehicleName: "",
      vehicleCode: "",
    };

    setSearch("");
    setFilters(cleared);
    setPage(1);
    loadList(1, limit, cleared);
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one job card to delete!");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} record(s)?`
    );
    if (!ok) return;

    try {
      setLoading(true);

      const { data } = await axios.post(
        `${API_BASE}/delete`,
        {
          ids: selectedIds,
          loginUserId: toInt(loginUserId, 0),
        },
        { headers: commonHeaders }
      );

      if (!data?.success) {
        throw new Error(data?.message || "Delete failed");
      }

      if ((data?.blocked || []).length > 0) {
        toast.warn(
          `Deleted: ${data?.deleted || 0}. Blocked (in use): ${
            data.blocked.length
          }`
        );
      } else {
        toast.success(data?.message || "Deleted successfully");
      }

      await loadList(page, limit);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Delete failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleView = (row) => {
    const id = row?.id;
    if (!id) {
      toast.error("Id not found for this row");
      return;
    }
    navigate(`/vehicle/JobCardEntryDetail?vehicleId=${encodeURIComponent(id)}`);
  };

  const handleEdit = (row) => {
    const id = row?.id;
    if (!id) {
      toast.error("Id not found for this row");
      return;
    }
    navigate(`/vehicle/JobCardEntry?vehicleId=${encodeURIComponent(id)}`);
  };

  const paginationText = useMemo(() => {
    const from = total === 0 ? 0 : (page - 1) * limit + 1;
    const to = Math.min(total, page * limit);
    return `${from}-${to} of ${total}`;
  }, [page, limit, total]);

  const isBusy = loading || pageLoading;

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-3xl">JOB CARD LISTING</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h2
              className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Job Card Listing
            </h2>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setShowFilters((prev) => !prev)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div
              className={`p-6 rounded-lg mb-6 ${
                isDark ? "bg-gray-800/50" : "bg-blue-50"
              }`}
            >
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? "text-white" : "text-blue-900"
                  }`}
                >
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleViewAll}
                    disabled={isBusy}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleSearchClick}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    value={filters.selectedBranchId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFilters((prev) => ({
                        ...prev,
                        selectedBranchId: val,
                        storeId: "Select",
                      }));

                      if (clean(branchType).toUpperCase() === "HO") {
                        loadStoresForBranch(val);
                      }
                    }}
                    className={inputClass}
                    disabled={clean(branchType).toUpperCase() !== "HO"}
                  >
                    {branches.map((b, idx) => (
                      <option key={`${b.value}-${idx}`} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Type</label>
                  <select
                    value={filters.vehicleTypeIndex}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        vehicleTypeIndex: e.target.value,
                      }))
                    }
                    className={inputClass}
                  >
                    {vehicleTypes.map((t, idx) => (
                      <option key={`${t.value}-${idx}`} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    value={filters.storeId}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        storeId: e.target.value,
                      }))
                    }
                    className={inputClass}
                  >
                    {stores.map((s, idx) => (
                      <option key={`${s.value}-${idx}`} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Reg. No</label>
                  <input
                    type="text"
                    value={filters.vehicleRegNo}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        vehicleRegNo: e.target.value,
                      }))
                    }
                    className={inputClass}
                    placeholder="Enter registration no"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    value={filters.vehicleName}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        vehicleName: e.target.value,
                      }))
                    }
                    className={inputClass}
                    placeholder="Enter vehicle name"
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    value={filters.vehicleCode}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        vehicleCode: e.target.value,
                      }))
                    }
                    className={inputClass}
                    placeholder="Enter vehicle code"
                  />
                </div>

                <div className="md:col-span-2 lg:col-span-3">
                  <label className={labelClass}>
                    Search (JobCard / Vehicle / Code)
                  </label>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearchClick();
                    }}
                    className={inputClass}
                    placeholder="Type and press Search"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || (loading && jobCards.length === 0) ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Loading...
                </div>
              </div>
            ) : jobCards.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  No Records found
                </div>
              </div>
            ) : (
              <>
                <table className={`w-full min-w-[1200px] ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        S.No
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Main Type
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Code
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Name
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Vehicle Reg No
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Job Card No
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">
                        Location
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold">
                        View
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold">
                        Edit
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          disabled={isBusy}
                          className="w-4 h-4 cursor-pointer"
                          title="Select All"
                        />
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    className={`${
                      isDark ? "divide-gray-700" : "divide-gray-200"
                    } divide-y`}
                  >
                    {jobCards.map((row, idx) => {
                      const checked = selectedIds.includes(row.id);

                      return (
                        <tr
                          key={row.id ?? idx}
                          className={`${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-white"
                              : isDark
                              ? "bg-gray-750"
                              : "bg-gray-50"
                          } transition-colors ${
                            isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                          }`}
                        >
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {(page - 1) * limit + idx + 1}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.vehicleMainType || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm font-medium ${
                              isDark ? "text-blue-400" : "text-blue-600"
                            }`}
                          >
                            {row.vehicleCode || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm font-medium ${
                              isDark ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {row.vehicleName || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.vehiclePortNo || "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm font-semibold ${
                              isDark ? "text-green-400" : "text-green-600"
                            }`}
                          >
                            {row.jobCardNo || "-"}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {row.status ? (
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  isDark
                                    ? "bg-yellow-900/40 text-yellow-200"
                                    : "bg-yellow-100 text-yellow-800"
                                }`}
                              >
                                {row.status}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td
                            className={`px-6 py-4 text-sm ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {row.location || "-"}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleView(row)}
                              disabled={isBusy}
                              className="text-blue-500 hover:text-blue-600 text-lg transition"
                              title="View"
                            >
                              <FaEye />
                            </button>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleEdit(row)}
                              disabled={isBusy}
                              className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSelect(row.id)}
                              disabled={isBusy}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <button
                    onClick={handleDelete}
                    disabled={selectedIds.length === 0 || isBusy}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                      selectedIds.length === 0 || isBusy
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    } bg-red-600 hover:bg-red-700 text-white`}
                  >
                    <FaTrash />
                    Remove
                  </button>

                  <div className="flex items-center gap-3 flex-wrap">
                    <div
                      className={`text-sm ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {paginationText}
                    </div>

                    <button
                      disabled={page <= 1 || isBusy}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className={`px-4 py-2 rounded-lg border text-sm font-semibold ${
                        page > 1 && !isBusy
                          ? isDark
                            ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                            : "border-gray-300 text-gray-700 hover:bg-white"
                          : "opacity-50 cursor-not-allowed border-gray-300"
                      }`}
                    >
                      Prev
                    </button>

                    <div
                      className={`text-sm ${
                        isDark ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Page {page} / {totalPages}
                    </div>

                    <button
                      disabled={page >= totalPages || isBusy}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      className={`px-4 py-2 rounded-lg border text-sm font-semibold ${
                        page < totalPages && !isBusy
                          ? isDark
                            ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                            : "border-gray-300 text-gray-700 hover:bg-white"
                          : "opacity-50 cursor-not-allowed border-gray-300"
                      }`}
                    >
                      Next
                    </button>

                    <select
                      value={limit}
                      onChange={(e) => {
                        setLimit(Number(e.target.value));
                        setPage(1);
                      }}
                      disabled={isBusy}
                      className={inputClass}
                      style={{ width: 120 }}
                    >
                      {[10, 20, 50, 100].map((n) => (
                        <option key={n} value={n}>
                          {n} / page
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default JobCardListing;